import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { politicaRetencaoService } from '@/services/politicaRetencaoService'
import { authStore } from '@/store/authStore'
import {
  OwnerRetencao,
  PoliticaRetencao,
  RegimeRetencao,
  TRIBUTOS_RETENCAO,
  TributoRetencao,
} from '@/types/politicaRetencao'
import { percentualPorExtenso } from '@/utils/percentualPorExtenso'
import { brl } from './estadoFinanceiro'
import { statusDoErro } from './errosNotaFiscal'
import { ROTULO_REGIME } from './notaFiscalRotulos'
import { SeloPublicacao } from './SeloPublicacao'

const ROTULO_REGIME_RETENCAO: Record<RegimeRetencao, string> = { ...ROTULO_REGIME, TODOS: 'Todos os regimes' }

const data = (iso?: string | null) => (iso ? dayjs(iso).format('DD/MM/YYYY') : '—')
const todos = (v: string) => (v === '*' ? 'Todos' : v)
/** Percentual pronto do backend, só com vírgula (nada convertido). */
const pct = (v: number | null | undefined) => (v != null ? `${String(v).replace('.', ',')}%` : '—')

/** Número digitado (vírgula ou ponto) → número; vazio ou inválido → null. Só leitura do campo. */
const numeroOuNulo = (texto: string): number | null => {
  const t = texto.trim().replace(',', '.')
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

const mensagem = (e: unknown, padrao: string) => (e as { message?: string })?.message ?? padrao

interface Props {
  /** FINANCEIRO ou ADMIN (o backend confere de novo e exige usuário sem escopo de tenant): propõe, aprova, encerra. */
  podeEditar: boolean
}

type Carga =
  | { estado: 'carregando' }
  | { estado: 'ok'; dados: PoliticaRetencao[]; pendentes: PoliticaRetencao[] }
  | { estado: 'erro'; mensagem: string }
  | { estado: 'semPermissao' }

interface Form {
  tributo: TributoRetencao
  regime: RegimeRetencao
  codigoServico: string
  municipioIbge: string
  retem: boolean
  /** PERCENTUAL digitado (0,65 = 0,65%). */
  aliquotaPercentual: string
  dispensa: string
  vigenciaInicio: string
  origem: string
  owner: OwnerRetencao | ''
}

const FORM_VAZIO = (): Form => ({
  tributo: 'ISS',
  regime: 'TODOS',
  codigoServico: '',
  municipioIbge: '',
  retem: true,
  aliquotaPercentual: '',
  dispensa: '',
  vigenciaInicio: dayjs().format('YYYY-MM-DD'),
  origem: '',
  owner: '',
})

/** Só versão ATIVA (ou sem status, anterior à V64) aberta pode ser encerrada. */
const encerravel = (p: PoliticaRetencao) =>
  !p.vigenciaFim && (p.statusPublicacao == null || p.statusPublicacao === 'ATIVA')

/**
 * Painel "Políticas de retenção" do Financeiro (AN-PP5 §6, §9; AN-PP6 §3/§8): versões da regra de retenção da NF
 * por tributo/regime/código de serviço/município (dado global, versionado).
 *
 * Publicação em duas pessoas (V64): "Nova versão" PROPÕE (202, PENDENTE_APROVACAO) e nada muda até OUTRA pessoa
 * FINANCEIRO/ADMIN aprovar no bloco "Propostas aguardando aprovação" — Aprovar/Rejeitar ficam desabilitados para
 * quem propôs (o backend é a trava: 403 `APROVACAO_PELO_PROPONENTE`). "Encerrar" só em versão ATIVA aberta.
 *
 * FIX-FIN A1: a alíquota é digitada em PERCENTUAL (0,65 = 0,65%) e vai como `aliquotaPercentual`; antes de enviar,
 * a tela mostra o percentual por extenso para confirmação. O backend converte para fração e recusa acima do teto do
 * tributo (422 `ALIQUOTA_ACIMA_DO_TETO`). A tabela exibe o `aliquotaPercentual` devolvido — nada calculado aqui.
 */
const PoliticasRetencaoPanel: FC<Props> = ({ podeEditar }) => {
  const { enqueueSnackbar } = useSnackbar()
  const usuarioId = authStore((s) => s.usuario?.id)
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' })
  const [filtroTributo, setFiltroTributo] = useState<TributoRetencao | ''>('')
  const [soVigentesHoje, setSoVigentesHoje] = useState(false)
  const [novo, setNovo] = useState<Form | null>(null)
  const [confirmando, setConfirmando] = useState(false)
  const [encerrando, setEncerrando] = useState<PoliticaRetencao | null>(null)
  const [vigenciaFim, setVigenciaFim] = useState('')
  const [rejeitando, setRejeitando] = useState<PoliticaRetencao | null>(null)
  const [motivoRejeicao, setMotivoRejeicao] = useState('')
  const [erroAcao, setErroAcao] = useState<string | null>(null)
  const [erroDecisao, setErroDecisao] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const carregar = useCallback(async () => {
    setCarga({ estado: 'carregando' })
    try {
      const tributo = filtroTributo || undefined
      // Pendentes nunca entram no filtro "vigentes hoje" do backend: com ele ligado, busca o histórico à parte.
      const historico = soVigentesHoje ? await politicaRetencaoService.listar({ tributo }) : null
      const dados =
        (await politicaRetencaoService.listar({
          tributo,
          vigenteEm: soVigentesHoje ? dayjs().format('YYYY-MM-DD') : undefined,
        })) ?? []
      const pendentes = (historico ?? dados).filter((p) => p.statusPublicacao === 'PENDENTE_APROVACAO')
      setCarga({ estado: 'ok', dados, pendentes })
    } catch (e) {
      setCarga(
        statusDoErro(e) === 403
          ? { estado: 'semPermissao' }
          : { estado: 'erro', mensagem: mensagem(e, 'Falha ao carregar as políticas.') }
      )
    }
  }, [filtroTributo, soVigentesHoje])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const propor = async () => {
    if (!novo) return
    setSalvando(true)
    setErroAcao(null)
    try {
      const proposta = await politicaRetencaoService.criar({
        tributo: novo.tributo,
        regimeTributario: novo.regime,
        codigoServico: novo.codigoServico.trim() || null,
        municipioIbge: novo.tributo === 'ISS' ? novo.municipioIbge.trim() || null : null,
        retem: novo.retem,
        aliquotaPercentual: novo.retem ? numeroOuNulo(novo.aliquotaPercentual) : null,
        dispensaAbaixoDe: novo.retem ? numeroOuNulo(novo.dispensa) : null,
        vigenciaInicio: novo.vigenciaInicio,
        origem: novo.origem.trim(),
        owner: novo.owner || null,
      })
      enqueueSnackbar(
        `Regra de ${proposta.tributo} v${proposta.versao} enviada para aprovação: só vale depois que outra pessoa aprovar.`,
        { variant: 'success' }
      )
      setNovo(null)
      setConfirmando(false)
      await carregar()
    } catch (e) {
      setConfirmando(false)
      setErroAcao(mensagem(e, 'Falha ao propor a regra.'))
    } finally {
      setSalvando(false)
    }
  }

  const aprovar = async (p: PoliticaRetencao) => {
    setSalvando(true)
    setErroDecisao(null)
    try {
      await politicaRetencaoService.aprovar(p.id, p.versaoRegistro ?? null)
      enqueueSnackbar(`Regra de ${p.tributo} v${p.versao} aprovada.`, { variant: 'success' })
      await carregar()
    } catch (e) {
      setErroDecisao(mensagem(e, 'Falha ao aprovar a regra.'))
    } finally {
      setSalvando(false)
    }
  }

  /** PP-final: o proponente retira a própria proposta — libera a chave para uma nova (como na simulação). */
  const retirar = async (p: PoliticaRetencao) => {
    setSalvando(true)
    setErroDecisao(null)
    try {
      await politicaRetencaoService.retirar(p.id)
      enqueueSnackbar(`Proposta de ${p.tributo} v${p.versao} retirada; fica no histórico como rejeitada.`, {
        variant: 'success',
      })
      await carregar()
    } catch (e) {
      setErroDecisao(mensagem(e, 'Falha ao retirar a proposta.'))
    } finally {
      setSalvando(false)
    }
  }

  const rejeitar = async () => {
    if (!rejeitando) return
    setSalvando(true)
    setErroAcao(null)
    try {
      await politicaRetencaoService.rejeitar(rejeitando.id, motivoRejeicao.trim(), rejeitando.versaoRegistro ?? null)
      enqueueSnackbar(`Proposta de ${rejeitando.tributo} v${rejeitando.versao} rejeitada.`, { variant: 'success' })
      setRejeitando(null)
      await carregar()
    } catch (e) {
      setErroAcao(mensagem(e, 'Falha ao rejeitar a proposta.'))
    } finally {
      setSalvando(false)
    }
  }

  const encerrar = async () => {
    if (!encerrando) return
    setSalvando(true)
    setErroAcao(null)
    try {
      await politicaRetencaoService.encerrar(encerrando.id, vigenciaFim || null)
      enqueueSnackbar(`Regra de ${encerrando.tributo} v${encerrando.versao} encerrada.`, { variant: 'success' })
      setEncerrando(null)
      await carregar()
    } catch (e) {
      setErroAcao(mensagem(e, 'Falha ao encerrar a regra.'))
    } finally {
      setSalvando(false)
    }
  }

  const extenso = novo?.retem ? percentualPorExtenso(novo.aliquotaPercentual) : null
  const formValido =
    !!novo &&
    !!novo.vigenciaInicio &&
    !!novo.origem.trim() &&
    (!novo.retem || (numeroOuNulo(novo.aliquotaPercentual) !== null && extenso !== null))
  const inicioNoPassado = !!novo?.vigenciaInicio && novo.vigenciaInicio < dayjs().format('YYYY-MM-DD')

  const filtros = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
      <TextField
        select
        size="small"
        label="Tributo"
        value={filtroTributo}
        onChange={(e) => setFiltroTributo(e.target.value as TributoRetencao | '')}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="">Todos</MenuItem>
        {TRIBUTOS_RETENCAO.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
      </TextField>
      <FormControlLabel
        control={<Checkbox checked={soVigentesHoje} onChange={(e) => setSoVigentesHoje(e.target.checked)} />}
        label="Só as vigentes hoje"
      />
      <Box sx={{ flex: 1 }} />
      {podeEditar && (
        <Button
          variant="contained"
          onClick={() => {
            setErroAcao(null)
            setConfirmando(false)
            setNovo(FORM_VAZIO())
          }}
        >
          Nova versão
        </Button>
      )}
    </Stack>
  )

  const hoje = dayjs().format('YYYY-MM-DD')
  const blocoPendentes =
    carga.estado === 'ok' && carga.pendentes.length > 0 ? (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Propostas aguardando aprovação
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Não valem na sugestão até OUTRA pessoa (Financeiro ou administrador) aprovar. Quem propôs não aprova a
            própria proposta. A aprovação precisa acontecer até o início da vigência: depois dele o backend recusa
            (409 VIGENCIA_EXPIRADA_NA_APROVACAO) e é preciso propor de novo.
          </Typography>
          {erroDecisao && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {erroDecisao}
            </Alert>
          )}
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Propostas de retenção pendentes">
              <TableHead>
                <TableRow>
                  <TableCell>Tributo</TableCell>
                  <TableCell>Regime</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Município</TableCell>
                  <TableCell align="right">Alíquota</TableCell>
                  <TableCell>Versão</TableCell>
                  <TableCell>Início</TableCell>
                  <TableCell>Área</TableCell>
                  <TableCell>Proposta por</TableCell>
                  <TableCell>Origem</TableCell>
                  {podeEditar && <TableCell align="right">Decisão</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {carga.pendentes.map((p) => {
                  const proprio = !!usuarioId && p.propostoPor === usuarioId
                  const expirada = p.vigenciaInicio < hoje
                  return (
                    <TableRow key={p.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{p.tributo}</TableCell>
                      <TableCell>{ROTULO_REGIME_RETENCAO[p.regimeTributario] ?? p.regimeTributario}</TableCell>
                      <TableCell>{todos(p.codigoServico)}</TableCell>
                      <TableCell>{todos(p.municipioIbge)}</TableCell>
                      <TableCell align="right">{p.retem ? pct(p.aliquotaPercentual) : 'Não retém'}</TableCell>
                      <TableCell>v{p.versao}</TableCell>
                      <TableCell>
                        {data(p.vigenciaInicio)}
                        {expirada && (
                          <Typography variant="caption" color="error" display="block">
                            Início já passou: rejeite e proponha de novo.
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{p.owner ?? '—'}</TableCell>
                      <TableCell>{proprio ? 'Você' : (p.propostoPor ?? '—')}</TableCell>
                      <TableCell>{p.origem}</TableCell>
                      {podeEditar && (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={salvando || proprio}
                            title={proprio ? 'Quem propôs não aprova a própria proposta.' : undefined}
                            onClick={() => void aprovar(p)}
                          >
                            Aprovar
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={salvando || proprio}
                            title={proprio ? 'Quem propôs não rejeita a própria proposta.' : undefined}
                            onClick={() => {
                              setErroAcao(null)
                              setMotivoRejeicao('')
                              setRejeitando(p)
                            }}
                            sx={{ ml: 0.5 }}
                          >
                            Rejeitar
                          </Button>
                          {proprio && (
                            <Button
                              size="small"
                              color="warning"
                              disabled={salvando}
                              onClick={() => void retirar(p)}
                              sx={{ ml: 0.5 }}
                            >
                              Retirar proposta
                            </Button>
                          )}
                          {proprio && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              Proposta sua: outra pessoa decide (ou retire para propor de novo).
                            </Typography>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    ) : null

  let corpo: JSX.Element
  if (carga.estado === 'carregando') corpo = <EstadoDeLista estado="carregando" minHeight={160} />
  else if (carga.estado === 'semPermissao') corpo = <EstadoDeLista estado="semPermissao" minHeight={160} />
  else if (carga.estado === 'erro') {
    corpo = (
      <EstadoDeLista
        estado="erro"
        mensagem="Não foi possível carregar as políticas de retenção"
        detalhe={carga.mensagem}
        acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
        minHeight={160}
      />
    )
  } else if (carga.dados.length === 0) {
    corpo = (
      <EstadoDeLista
        estado="vazio"
        mensagem="Nenhuma regra de retenção cadastrada"
        detalhe="Sem regra, a sugestão de retenções da NF fica em branco e o Financeiro digita os valores."
        minHeight={160}
      />
    )
  } else {
    corpo = (
      <Card>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Políticas de retenção">
            <TableHead>
              <TableRow>
                <TableCell>Tributo</TableCell>
                <TableCell>Regime</TableCell>
                <TableCell>Código de serviço</TableCell>
                <TableCell>Município (IBGE)</TableCell>
                <TableCell align="right">Alíquota</TableCell>
                <TableCell align="right">Dispensa abaixo de</TableCell>
                <TableCell>Versão</TableCell>
                <TableCell>Vigência</TableCell>
                <TableCell>Origem</TableCell>
                {podeEditar && <TableCell align="right">Ações</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {carga.dados.map((p) => (
                <TableRow key={p.id}>
                  <TableCell sx={{ fontWeight: 600 }}>{p.tributo}</TableCell>
                  <TableCell>{ROTULO_REGIME_RETENCAO[p.regimeTributario] ?? p.regimeTributario}</TableCell>
                  <TableCell>{todos(p.codigoServico)}</TableCell>
                  <TableCell>{todos(p.municipioIbge)}</TableCell>
                  <TableCell align="right">{p.retem ? pct(p.aliquotaPercentual) : 'Não retém'}</TableCell>
                  <TableCell align="right">{p.dispensaAbaixoDe != null ? brl(p.dispensaAbaixoDe) : '—'}</TableCell>
                  <TableCell>
                    v{p.versao}
                    {p.vigenteHoje && <Chip size="small" color="success" label="vigente" sx={{ ml: 0.5 }} />}
                    <SeloPublicacao politica={p} />
                  </TableCell>
                  <TableCell>
                    {data(p.vigenciaInicio)} → {p.vigenciaFim ? data(p.vigenciaFim) : 'em aberto'}
                    {p.statusPublicacao === 'REJEITADA' && p.motivoRejeicao && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        Rejeitada: {p.motivoRejeicao}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{p.origem}</TableCell>
                  {podeEditar && (
                    <TableCell align="right">
                      {encerravel(p) && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setErroAcao(null)
                            setVigenciaFim('')
                            setEncerrando(p)
                          }}
                        >
                          Encerrar
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    )
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="textSecondary">
        Retenções do consultor na fonte da NF de comissão (sugestão para a aprovação fiscal; quem confirma
        é o Financeiro). Tributos próprios da Originei não entram aqui.
      </Typography>
      {filtros}
      {blocoPendentes}
      {corpo}

      <Dialog
        open={novo !== null}
        onClose={() => {
          setNovo(null)
          setConfirmando(false)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nova versão de regra de retenção</DialogTitle>
        <DialogContent dividers>
          {novo && !confirmando && (
            <Stack spacing={2}>
              {erroAcao && <Alert severity="error">{erroAcao}</Alert>}
              <Typography variant="body2" color="textSecondary">
                A nova versão vai para aprovação de outra pessoa (Financeiro ou administrador). Só quando aprovada ela
                passa a valer e fecha a versão aberta da mesma chave (tributo, regime, código, município) na data de
                início desta.
              </Typography>
              <TextField
                select
                label="Tributo"
                value={novo.tributo}
                onChange={(e) => setNovo({ ...novo, tributo: e.target.value as TributoRetencao })}
              >
                {TRIBUTOS_RETENCAO.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Regime do prestador"
                value={novo.regime}
                onChange={(e) => setNovo({ ...novo, regime: e.target.value as RegimeRetencao })}
              >
                {(Object.keys(ROTULO_REGIME_RETENCAO) as RegimeRetencao[]).map((r) => (
                  <MenuItem key={r} value={r}>
                    {ROTULO_REGIME_RETENCAO[r]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Código de serviço (LC 116)"
                value={novo.codigoServico}
                onChange={(e) => setNovo({ ...novo, codigoServico: e.target.value })}
                helperText="Ex.: 17.06 (gravado só com dígitos: 1706). Vazio = todos os códigos."
              />
              {novo.tributo === 'ISS' && (
                <TextField
                  label="Município de incidência (IBGE)"
                  value={novo.municipioIbge}
                  onChange={(e) => setNovo({ ...novo, municipioIbge: e.target.value })}
                  inputProps={{ maxLength: 7 }}
                  helperText="7 dígitos. Vazio = todos os municípios."
                />
              )}
              <FormControlLabel
                control={<Checkbox checked={novo.retem} onChange={(e) => setNovo({ ...novo, retem: e.target.checked })} />}
                label="Retém (desmarque para registrar uma regra de não retenção)"
              />
              {novo.retem && (
                <>
                  <TextField
                    label="Alíquota (%)"
                    value={novo.aliquotaPercentual}
                    onChange={(e) => setNovo({ ...novo, aliquotaPercentual: e.target.value })}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    error={!!novo.aliquotaPercentual.trim() && extenso === null}
                    helperText={
                      novo.aliquotaPercentual.trim() && extenso === null
                        ? 'Informe o percentual com até 4 casas decimais (ex.: 0,65).'
                        : 'Em percentual: 0,65 = 0,65% (PIS). O backend recusa acima do teto do tributo.'
                    }
                  />
                  <TextField
                    label="Dispensa abaixo de (R$)"
                    value={novo.dispensa}
                    onChange={(e) => setNovo({ ...novo, dispensa: e.target.value })}
                    helperText="Retenção calculada até este valor é dispensada. Vazio = sem dispensa."
                  />
                </>
              )}
              <TextField
                label="Início da vigência"
                type="date"
                value={novo.vigenciaInicio}
                onChange={(e) => setNovo({ ...novo, vigenciaInicio: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: hoje }}
                error={inicioNoPassado}
                helperText={
                  inicioNoPassado
                    ? 'Início no passado: o backend recusa (VIGENCIA_RETROATIVA).'
                    : 'Hoje ou futura. A aprovação precisa sair até esta data.'
                }
              />
              <TextField
                select
                label="Área dona (opcional)"
                value={novo.owner}
                onChange={(e) => setNovo({ ...novo, owner: e.target.value as OwnerRetencao | '' })}
              >
                <MenuItem value="">Padrão (Financeiro)</MenuItem>
                <MenuItem value="FINANCEIRO">Financeiro</MenuItem>
                <MenuItem value="JURIDICO">Jurídico</MenuItem>
              </TextField>
              <TextField
                label="Origem (fundamento)"
                value={novo.origem}
                onChange={(e) => setNovo({ ...novo, origem: e.target.value })}
                inputProps={{ maxLength: 200 }}
                helperText='Ex.: "Parecer Jurídico 2026-xx", "IN RFB …".'
              />
            </Stack>
          )}
          {novo && confirmando && (
            <Stack spacing={2}>
              <Typography variant="body1">
                Confirme a regra de <strong>{novo.tributo}</strong> antes de enviar para aprovação:
              </Typography>
              {novo.retem ? (
                <Alert severity="warning">
                  Alíquota de <strong>{novo.aliquotaPercentual.trim()}%</strong> — <strong>{extenso}</strong>.
                </Alert>
              ) : (
                <Alert severity="info">Regra de NÃO retenção (sem alíquota).</Alert>
              )}
              <Typography variant="body2" color="textSecondary">
                Início em {data(novo.vigenciaInicio)}. Se o percentual não for este, volte e corrija.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {confirmando ? (
            <>
              <Button onClick={() => setConfirmando(false)}>Corrigir</Button>
              <Button variant="contained" onClick={() => void propor()} disabled={salvando}>
                {salvando ? 'Enviando…' : 'Confirmar e enviar'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setNovo(null)}>Voltar</Button>
              <Button variant="contained" onClick={() => setConfirmando(true)} disabled={salvando || !formValido}>
                Enviar para aprovação
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={rejeitando !== null} onClose={() => setRejeitando(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          Rejeitar {rejeitando?.tributo} v{rejeitando?.versao}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {erroAcao && <Alert severity="error">{erroAcao}</Alert>}
            <TextField
              label="Motivo da rejeição"
              value={motivoRejeicao}
              onChange={(e) => setMotivoRejeicao(e.target.value)}
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejeitando(null)}>Voltar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void rejeitar()}
            disabled={salvando || !motivoRejeicao.trim()}
          >
            {salvando ? 'Rejeitando…' : 'Rejeitar proposta'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={encerrando !== null} onClose={() => setEncerrando(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          Encerrar {encerrando?.tributo} v{encerrando?.versao}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {erroAcao && <Alert severity="error">{erroAcao}</Alert>}
            <TextField
              label="Deixa de valer em"
              type="date"
              value={vigenciaFim}
              onChange={(e) => setVigenciaFim(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Primeiro dia sem a regra. Vazio = hoje."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEncerrando(null)}>Voltar</Button>
          <Button variant="contained" color="error" onClick={() => void encerrar()} disabled={salvando}>
            {salvando ? 'Encerrando…' : 'Encerrar regra'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default PoliticasRetencaoPanel
